import { useState } from 'react';

import {
  Button,
  Form,
  FormField,
  Heading,
  Input,
  Text,
} from '@livechat/design-system-react-components';

import Config from 'Config';
import { SigninMfaFormData, FormStates } from 'types/form';
import { submitPost } from 'helpers/form';
import { buildOauthQuery } from 'helpers/oath-query-builder';

import { SigninException } from './SigninException';

import * as styles from './styles';

type Props = {
  formData: SigninMfaFormData;
  onDataChange: (SigninMfaData: SigninMfaFormData) => void;
};

export const MFAForm = ({ formData, onDataChange }: Props) => {
  const [mfaValidationError, setMfaValidationError] = useState<null | string>(null);
  const [formState, setFormState] = useState(FormStates.Default);

  const handleMfaInputChange = (e) => {
    onDataChange({ ...formData, mfa_challenge: e.target.value.trim() });

    if (e.target.value.trim() === '') {
      setMfaValidationError('This field is required');
    } else {
      setMfaValidationError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.mfa_challenge || formData.mfa_challenge?.trim() === '') {
      setMfaValidationError('This field is required');

      return;
    }

    setFormState(FormStates.Requesting);

    try {
      submitPost(`${Config.accounts_api_url}/?${buildOauthQuery(Config)}`, {
        mfa_token: formData.mfa_token,
        mfa_challenge: formData.mfa_challenge,
      });
    } catch (error: any) {
      throw new Error(`Couldn't send MFA code, ${error.message}`);
    }
  };

  return (
    <div>
      <div css={styles.container}>
        <Heading size="lg">Log in</Heading>
        <Text css={styles.description}>
          Enter the verification code from your authentication app.
        </Text>
        <Form onSubmit={handleSubmit}>
          <SigninException />

          <FormField
            labelText="Verification code"
            labelFor="mfa_challenge"
            css={styles.formField}
            error={mfaValidationError}
          >
            <Input
              type="text"
              name="mfa_challenge"
              placeholder="Enter verification code"
              inputSize="large"
              value={formData.mfa_challenge}
              onChange={handleMfaInputChange}
              autoFocus
            />
          </FormField>

          <Button
            type="submit"
            kind="destructive"
            size="large"
            loading={formState === FormStates.Requesting}
            css={styles.mfaButton}
          >
            Verify
          </Button>
        </Form>
      </div>
    </div>
  );
};
